import React, { useEffect, useState, useRef } from "react";
import { FileText, Plus, Trash2, Power, Star, ChevronDown, ChevronUp, Loader2, Eye, Edit3, Copy, MessageSquare, MessageCircle, Mail, Hash, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CHANNEL_ICONS = { sms: MessageSquare, whatsapp: MessageCircle, email: Mail };
const CHANNEL_COLORS = {
  sms: { badge: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  whatsapp: { badge: "bg-green-100 text-green-700", dot: "bg-green-500" },
  email: { badge: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500" },
};

const CATEGORY_COLORS = {
  otp: "from-violet-500 to-purple-600",
  registration: "from-emerald-500 to-teal-600",
  password_reset: "from-amber-500 to-orange-600",
  promotional: "from-pink-500 to-rose-600",
  transactional: "from-blue-500 to-cyan-600",
  invoice: "from-slate-500 to-gray-600",
  reminder: "from-red-500 to-orange-600",
};

/* ── Template Card ───────────────────────────────────────────────── */
function TemplateCard({ t, onEdit, onDelete, onPreview, onToggleDefault }) {
  const [expanded, setExpanded] = useState(false);
  const ChIcon = CHANNEL_ICONS[t.channel] || MessageSquare;
  const cc = CHANNEL_COLORS[t.channel] || CHANNEL_COLORS.sms;

  const highlightVars = (text) => {
    if (!text) return "";
    return text.replace(/\{\{(\w+)\}\}/g, '<span class="bg-brand-primary/10 text-brand-primary font-semibold px-1 rounded">{{$1}}</span>');
  };

  return (
    <div className={`bg-white rounded-xl border ${t.is_active ? "border-slate-200" : "border-slate-100 opacity-60"} shadow-sm transition-all hover:shadow-md`}>
      <div className="px-4 py-3 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${CATEGORY_COLORS[t.category] || "from-slate-400 to-slate-500"} flex items-center justify-center shrink-0`}>
          <FileText className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-800 truncate">{t.name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cc.badge} inline-flex items-center gap-1`}>
              <ChIcon className="w-3 h-3" /> {t.channel?.toUpperCase()}
            </span>
            {t.is_default && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                <Star className="w-3 h-3" /> Default
              </span>
            )}
            {t.dlt_template_id && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 inline-flex items-center gap-1">
                <Hash className="w-3 h-3" /> DLT
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-0.5 truncate font-mono">{t.category} · {t.variables?.length || 0} vars</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!t.is_default && t.is_active && (
            <button onClick={() => onToggleDefault(t)} title="Set Default" className="p-1.5 rounded-md text-amber-400 hover:bg-amber-50 hover:text-amber-600 transition-colors">
              <Star className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onPreview(t)} title="Preview" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-brand-primary transition-colors">
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={() => onEdit(t)} title="Edit" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-brand-primary transition-colors">
            <Edit3 className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(t)} title="Delete" className="p-1.5 rounded-md text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 transition-colors">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Template Body</div>
          <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap font-mono leading-relaxed"
            dangerouslySetInnerHTML={{ __html: highlightVars(t.body) }} />
          {t.subject && (
            <div className="mt-2">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Subject</div>
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 font-mono"
                dangerouslySetInnerHTML={{ __html: highlightVars(t.subject) }} />
            </div>
          )}
          {t.dlt_template_id && (
            <div className="mt-2 text-xs text-purple-600 font-mono">DLT ID: {t.dlt_template_id}</div>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(t.variables || []).map(v => (
              <span key={v} className="text-[10px] bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded-full font-mono">{`{{${v}}}`}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Add/Edit Modal ──────────────────────────────────────────────── */
function TemplateModal({ template, categories, onClose, onSaved }) {
  const isEdit = !!template?.id;
  const [form, setForm] = useState({
    category: template?.category || "otp",
    channel: template?.channel || "sms",
    name: template?.name || "",
    body: template?.body || "",
    subject: template?.subject || "",
    dlt_template_id: template?.dlt_template_id || "",
    is_default: template?.is_default || false,
    is_active: template?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const bodyRef = useRef(null);

  const catMeta = categories[form.category] || {};
  const availableVars = catMeta.variables || [];

  const insertVar = (varName) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = form.body;
    const insert = `{{${varName}}}`;
    setForm(p => ({ ...p, body: text.substring(0, start) + insert + text.substring(end) }));
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + insert.length, start + insert.length);
    }, 50);
  };

  const doPreview = async () => {
    try {
      if (isEdit) {
        const { data } = await api.post(`/superadmin/notification/templates/${template.id}/preview`, {});
        setPreview(data);
      } else {
        // Local preview with sample data
        let rendered = form.body;
        const sample = catMeta.sample_data || {};
        Object.entries(sample).forEach(([k, v]) => {
          rendered = rendered.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), v);
        });
        setPreview({ rendered_body: rendered, char_count: rendered.length, sms_parts: Math.max(1, Math.ceil(rendered.length / 160)) });
      }
    } catch { toast.error("Preview failed"); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.body.trim()) { toast.error("Body is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/superadmin/notification/templates/${template.id}`, form);
        toast.success("Template updated");
      } else {
        await api.post("/superadmin/notification/templates", form);
        toast.success("Template created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h4 className="font-heading font-bold text-lg text-brand-nav">{isEdit ? "Edit Template" : "New Message Template"}</h4>
            <p className="text-sm text-slate-500 mt-0.5">Define message body with {"{{variables}}"} placeholders</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Row 1: Category + Channel */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30">
                {Object.keys(categories).length > 0 ? (
                  Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)
                ) : (
                  <option value="otp">OTP / Verification</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Channel *</label>
              <select value={form.channel} onChange={e => setForm(p => ({ ...p, channel: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30">
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
              </select>
            </div>
          </div>

          {/* Template Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Template Name *</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. OTP Verification SMS"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
          </div>

          {/* Subject (email only) */}
          {form.channel === "email" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email Subject</label>
              <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. Your OTP Code"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
            </div>
          )}

          {/* Variable Buttons */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Insert Variable</div>
            <div className="flex flex-wrap gap-1.5">
              {availableVars.map(v => (
                <button key={v} type="button" onClick={() => insertVar(v)}
                  className="text-xs bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 px-2.5 py-1 rounded-full font-mono transition-colors">
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Message Body *</label>
            <textarea ref={bodyRef} rows={5} value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
              placeholder="Type your message... Use {{variable}} for dynamic content"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-primary/30 resize-y" />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">{form.body.length} chars · {Math.max(1, Math.ceil(form.body.length / 160))} SMS part(s)</span>
              <button type="button" onClick={doPreview} className="text-xs text-brand-primary hover:underline inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> Preview
              </button>
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Live Preview
              </div>
              <div className="bg-white rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap border border-slate-100">{preview.rendered_body}</div>
              <div className="text-xs text-slate-400 mt-1.5">{preview.char_count} chars · {preview.sms_parts} SMS part(s)</div>
            </div>
          )}

          {/* DLT Template ID */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">DLT Template ID <span className="text-slate-400 font-normal">(for DLT/MSG compliance)</span></label>
            <input value={form.dlt_template_id} onChange={e => setForm(p => ({ ...p, dlt_template_id: e.target.value }))}
              placeholder="e.g. 1107168XXXXXX"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} className="rounded" />
              <span className="text-sm text-slate-700">Set as default for {form.category}/{form.channel}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="rounded" />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 rounded-lg py-2.5 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
          <button onClick={save} disabled={saving}
            className="flex-1 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-lg py-2.5 font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Update Template" : "Create Template"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Preview Modal ───────────────────────────────────────────────── */
function PreviewModal({ template, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/superadmin/notification/templates/${template.id}/preview`, {});
        setPreview(data);
      } catch { toast.error("Preview failed"); }
      finally { setLoading(false); }
    })();
  }, [template.id]);

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h4 className="font-heading font-bold text-lg text-brand-nav">Template Preview</h4>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-semibold text-slate-700">{template.name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${CHANNEL_COLORS[template.channel]?.badge || ""}`}>{template.channel?.toUpperCase()}</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
          ) : preview ? (
            <>
              {preview.rendered_subject && (
                <div className="mb-2">
                  <div className="text-xs font-semibold text-slate-500 mb-1">Subject</div>
                  <div className="bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-800">{preview.rendered_subject}</div>
                </div>
              )}
              <div className="text-xs font-semibold text-slate-500 mb-1">Message</div>
              <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-800 whitespace-pre-wrap">{preview.rendered_body}</div>
              <div className="text-xs text-slate-400 mt-2">{preview.char_count} characters · {preview.sms_parts} SMS part(s)</div>
            </>
          ) : <p className="text-sm text-slate-400">Failed to load preview.</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="w-full border border-slate-200 text-slate-600 rounded-lg py-2.5 font-semibold hover:bg-slate-50 transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────────── */
export default function MessageTemplates() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeChannel, setActiveChannel] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, cRes] = await Promise.all([
        api.get("/superadmin/notification/templates"),
        api.get("/superadmin/notification/templates/categories"),
      ]);
      setTemplates(tRes.data);
      setCategories(cRes.data);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (t) => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/superadmin/notification/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const handleSetDefault = async (t) => {
    try {
      await api.put(`/superadmin/notification/templates/${t.id}`, { is_default: true });
      toast.success(`${t.name} set as default`);
      load();
    } catch { toast.error("Failed to set default"); }
  };

  const filtered = templates.filter(t => {
    if (activeCategory !== "all" && t.category !== activeCategory) return false;
    if (activeChannel !== "all" && t.channel !== activeChannel) return false;
    return true;
  });

  const categoryTabs = ["all", ...Object.keys(categories)];
  const channelTabs = ["all", "sms", "whatsapp", "email"];

  return (
    <div className="mt-8">
      {/* Section Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-heading font-bold text-xl text-brand-nav flex items-center gap-2">
            <FileText className="w-5 h-5" /> Message Templates
          </h3>
          <p className="text-slate-500 text-sm mt-1">Manage SMS, WhatsApp & Email templates for OTP, registration, promotions, and more. Use {"{{variables}}"} for dynamic content.</p>
        </div>
        <button 
          onClick={() => { setEditTemplate(null); setShowModal(true); }}
          disabled={Object.keys(categories).length === 0 && !loading}
          className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold text-sm px-4 py-2 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={Object.keys(categories).length === 0 ? "Please wait for categories to load or check backend connection" : "Create new template"}
        >
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {/* Category Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
        {Object.entries(categories).map(([key, meta]) => {
          const count = templates.filter(t => t.category === key).length;
          const activeCount = templates.filter(t => t.category === key && t.is_active).length;
          return (
            <button key={key} onClick={() => setActiveCategory(activeCategory === key ? "all" : key)}
              className={`text-left p-3 rounded-xl border transition-all ${activeCategory === key ? "border-brand-primary bg-brand-primary/5 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${CATEGORY_COLORS[key] || "from-slate-400 to-slate-500"} flex items-center justify-center mb-1.5`}>
                <FileText className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="text-xs font-semibold text-slate-700 truncate">{meta.label}</div>
              <div className="text-[10px] text-slate-400">{activeCount}/{count} active</div>
            </button>
          );
        })}
      </div>

      {/* Channel Filter */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit">
        {channelTabs.map(ch => (
          <button key={ch} onClick={() => setActiveChannel(ch)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all capitalize ${activeChannel === ch ? "bg-white text-brand-nav shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {ch === "all" ? `All (${templates.filter(t => activeCategory === "all" || t.category === activeCategory).length})` : `${ch.toUpperCase()} (${templates.filter(t => t.channel === ch && (activeCategory === "all" || t.category === activeCategory)).length})`}
          </button>
        ))}
      </div>

      {/* Template List */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <FileText className="w-6 h-6 text-slate-400" />
          </div>
          <p className="font-semibold text-slate-600">No templates found</p>
          <p className="text-sm text-slate-400 mt-1">Create your first message template.</p>
          {Object.keys(categories).length === 0 && !loading && (
             <p className="text-xs text-amber-600 mt-2 bg-amber-50 mx-auto w-fit px-3 py-1.5 rounded-md border border-amber-200">
               Could not load template categories. Is the backend updated and running?
             </p>
          )}
          <button 
            onClick={() => { setEditTemplate(null); setShowModal(true); }}
            disabled={Object.keys(categories).length === 0 && !loading}
            className="mt-4 inline-flex items-center gap-2 bg-brand-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-brand-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" /> New Template
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map(t => (
            <TemplateCard key={t.id} t={t}
              onEdit={(t) => { setEditTemplate(t); setShowModal(true); }}
              onDelete={handleDelete}
              onPreview={(t) => setPreviewTemplate(t)}
              onToggleDefault={handleSetDefault}
            />
          ))}
        </div>
      )}

      {showModal && (
        <TemplateModal
          template={editTemplate}
          categories={categories}
          onClose={() => { setShowModal(false); setEditTemplate(null); }}
          onSaved={load}
        />
      )}
      {previewTemplate && (
        <PreviewModal template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      )}
    </div>
  );
}
